import type { CustomChartGrammar } from '@erp/viz/custom';
import type { ChartData } from '@erp/viz';

export type CustomSpecCheck =
  | {
      spec: Record<string, unknown>;
      error: null;
      /** Doubts the proof could not settle (it ran on a subset of rows). */
      warnings: string[];
    }
  | { spec: null; error: string };

/**
 * The most rows the render proof runs on. A Report dataset in the browser is
 * at most the Client row limit (10,000) or a Server aggregation (5,000 groups),
 * well under this, so the proof sees every row the chart will render (100,000
 * render headlessly in ~17 ms). Past it the proof runs on a subset, where an
 * empty draw only warns.
 */
const PROOF_ROW_LIMIT = 100_000;

/**
 * The single gate for a Custom chart spec entering a Report — pasted, AI
 * generated, or inside a generated canvas layout. Validates it for its grammar
 * (Vega-Lite compiles; Vega passes the official schema and the parser; neither
 * may reach outside the chart), then, given the rows the chart will render —
 * all of them, never a sample — proves it renders against them headlessly. Returns the spec as it should be
 * persisted (host-owned keys stripped) or every reason it was refused.
 *
 * Async because it loads the vega chunk on demand — already cached whenever a
 * Custom chart is on screen.
 */
export async function validateCustomSpec(
  grammar: CustomChartGrammar,
  spec: unknown,
  rows?: ChartData,
): Promise<CustomSpecCheck> {
  const { validateCustomChart, verifyCustomChartRenders } =
    await import('@erp/viz/custom');
  const validation = validateCustomChart({ grammar, spec });
  if (!validation.ok) {
    return { spec: null, error: validation.errors.join('\n') };
  }
  if (!rows) return { spec: validation.chart.spec, error: null, warnings: [] };
  const complete = rows.length <= PROOF_ROW_LIMIT;
  const proof = await verifyCustomChartRenders(
    validation.chart,
    complete ? rows : rows.slice(0, PROOF_ROW_LIMIT),
    { complete },
  );
  if (proof.errors.length > 0) {
    return { spec: null, error: proof.errors.join('\n') };
  }
  return { spec: validation.chart.spec, error: null, warnings: proof.warnings };
}

/**
 * Parses config-form input: must be a JSON object, then passes
 * {@link validateCustomSpec}.
 */
export async function parseCustomSpecDraft(
  grammar: CustomChartGrammar,
  draft: string,
  rows?: ChartData,
): Promise<CustomSpecCheck> {
  let parsed: unknown;
  try {
    parsed = JSON.parse(draft);
  } catch (error) {
    return {
      spec: null,
      error: error instanceof Error ? error.message : String(error),
    };
  }
  return validateCustomSpec(grammar, parsed, rows);
}

interface GeneratedChart {
  grammar: CustomChartGrammar;
  spec: Record<string, unknown>;
}

interface RepairAttempt {
  previousAttempt: GeneratedChart & { error: string };
}

/**
 * The AI loop: generate a spec, validate it against the dataset's rows, and
 * on failure ask once more with the validator's reasons. Resolves to the
 * validated chart, or the reasons the last attempt was refused.
 */
export async function generateValidatedChart<Request extends object>(
  generate: (
    request: Request | (Request & RepairAttempt),
  ) => Promise<GeneratedChart>,
  request: Request,
  rows: ChartData,
): Promise<
  { chart: GeneratedChart; error: null } | { chart: null; error: string }
> {
  let generated = await generate(request);
  let check = await validateCustomSpec(generated.grammar, generated.spec, rows);
  if (check.error !== null) {
    generated = await generate({
      ...request,
      previousAttempt: {
        spec: generated.spec,
        grammar: generated.grammar,
        error: check.error.slice(0, 4000),
      },
    });
    check = await validateCustomSpec(generated.grammar, generated.spec, rows);
  }
  return check.error !== null
    ? { chart: null, error: check.error }
    : { chart: { grammar: generated.grammar, spec: check.spec }, error: null };
}
